import { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import { PillInput } from 'src/components/common/pills/PillInput';
import {
  XIcon,
  ArrowUUpLeftIcon,
  PencilIcon,
  WarningIcon,
  ArrowLeftIcon,
  CaretDownIcon,
  FloppyDiskIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { cn } from 'src/lib/utils';

interface WorkflowEditorHeaderProps {
  workflowName: string;
  workflowDescription: string;
  workflowTags: string[];
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onTagsChange: (tags: string[]) => void;
  isCreateMode: boolean;
  isSaving: boolean;
  isDirty: boolean;
  hasTrigger: boolean;
  onSave: () => void;
  onReset: () => void;
  onCancel: () => void;
}

export function WorkflowEditorHeader({
  workflowName,
  workflowDescription,
  workflowTags,
  onNameChange,
  onDescriptionChange,
  onTagsChange,
  isCreateMode,
  isSaving,
  isDirty,
  hasTrigger,
  onSave,
  onReset,
  onCancel,
}: WorkflowEditorHeaderProps) {
  const [isEditingName, setIsEditingName] = useState(isCreateMode);
  const [isDetailsOpen, setIsDetailsOpen] = useState(isCreateMode);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        setIsEditingName(false);
      } else if (e.key === 'Escape') {
        setIsEditingName(false);
      }
    },
    [],
  );

  const handleNameBlur = useCallback(() => {
    setIsEditingName(false);
  }, []);

  const handleDetailsToggle = useCallback(() => {
    setIsDetailsOpen((open) => !open);
  }, []);

  return (
    <div className="flex-shrink-0 border-b bg-background">
      {!hasTrigger && (
        <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
          <WarningIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          <span className="text-xs text-amber-700 dark:text-amber-300">
            No trigger - workflow won't run automatically
          </span>
        </div>
      )}

      <div className="flex items-center justify-between px-4 pb-1.5">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={onCancel}
            disabled={isSaving}
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>

          {isEditingName ? (
            <Input
              ref={nameInputRef}
              value={workflowName}
              onChange={(e) => onNameChange(e.target.value)}
              onKeyDown={handleNameKeyDown}
              onBlur={handleNameBlur}
              placeholder="Workflow name..."
              disabled={isSaving}
              className={cn(
                'text-lg font-semibold h-8 max-w-[300px]',
                isCreateMode && !workflowName.trim() && 'border-destructive',
              )}
            />
          ) : (
            <button
              type="button"
              onClick={() => setIsEditingName(true)}
              disabled={isSaving}
              className={cn(
                'flex items-center gap-2 text-lg font-semibold truncate',
                'hover:text-primary transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span className="truncate max-w-[300px]">
                {workflowName || 'Untitled Workflow'}
              </span>
              <PencilIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            </button>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={handleDetailsToggle}
            disabled={isSaving}
          >
            <span className="text-xs">Details</span>
            <CaretDownIcon
              className={cn(
                'h-3.5 w-3.5 ml-1 transition-transform',
                isDetailsOpen && '-rotate-180',
              )}
            />
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isDirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded">
              Unsaved
            </span>
          )}
          <Badge variant="secondary" className="text-xs">
            {isCreateMode ? 'Draft' : 'Editing'}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={!isDirty || isSaving}
            className="h-8"
          >
            <ArrowUUpLeftIcon className="w-4 h-4 mr-1" />
            Reset
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isSaving}
            className="h-8"
          >
            <XIcon className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            disabled={isSaving || (!isDirty && !isCreateMode)}
            className="h-8"
          >
            {isSaving ? (
              <CircleNotchIcon className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <FloppyDiskIcon className="w-4 h-4 mr-1" />
            )}
            {isCreateMode ? 'Create' : 'Save'}
          </Button>
        </div>
      </div>

      {isDetailsOpen && (
        <div className="px-4 pb-3 pt-1 border-t">
          <div className="grid gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Description
              </label>
              <Textarea
                value={workflowDescription}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder="Add a description..."
                disabled={isSaving}
                rows={2}
                className="mt-1.5 text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Tags
              </label>
              <div className="mt-1.5">
                <PillInput
                  values={workflowTags}
                  onChange={onTagsChange}
                  placeholder="Add tags..."
                  className="text-xs"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
